import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

interface DateFilterButtonProps {
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
  labelText: string;
  useCase?: string;
}

const DateFilterButton: React.FC<DateFilterButtonProps> = ({
  onStartDateChange,
  onEndDateChange,
  startDate,
  endDate,
  labelText,
  useCase,
}) => {
  const { t } = useTranslation();
  const [showFilterDatePopup, setShowFilterDatePopup] = useState(false);
  const filterDatePopupRef = useRef<HTMLDivElement>(null);
  const [currentDate, setCurrentDate] = useState<Date | null>(new Date());

  const toggleFilterDatePopup = () => {
    setShowFilterDatePopup(!showFilterDatePopup);
  };

  const handleOutsideClick = (event: MouseEvent) => {
    if (
      filterDatePopupRef.current &&
      !filterDatePopupRef.current.contains(event.target as Node)
    ) {
      setShowFilterDatePopup(false);
    }
  };

  const handleRemoveDateFilters = () => {
    onStartDateChange(null);
    onEndDateChange(null);
    setShowFilterDatePopup(false);
  };

  React.useEffect(() => {
    if (showFilterDatePopup) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showFilterDatePopup]);

  return (
    <>
      <button
        className="mr-2 rounded-md bg-white px-2 py-1 text-sm text-gray-700 shadow hover:bg-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 lg:px-4 lg:py-2 lg:text-base"
        onClick={toggleFilterDatePopup}
      >
        <FontAwesomeIcon icon={faCalendarAlt} className="mr-2" />
        {startDate && endDate
          ? `${format(startDate, "MM/dd/yyyy")} - ${format(
              endDate,
              "MM/dd/yyyy",
            )}`
          : startDate
            ? `After ${format(startDate, "MM/dd/yyyy")}`
            : endDate
              ? `Before ${format(endDate, "MM/dd/yyyy")}`
              : labelText}
      </button>
      {showFilterDatePopup && (
        <div
          ref={filterDatePopupRef}
          className="absolute z-10 mt-2 w-80 rounded-md bg-white shadow-lg"
        >
          <div className="p-4">
            <h3 className="mb-2 text-lg font-semibold">
              {t("back.management.component.filterDates")}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                {t("back.management.component.startDate")}
              </label>
              <div className="flex items-center gap-2">
                <ReactDatePicker
                  selected={startDate}
                  onChange={onStartDateChange}
                  maxDate={
                    useCase === "appointment"
                      ? undefined
                      : (currentDate ?? undefined)
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  placeholderText={t(
                    "back.management.component.selectStartDate",
                  )}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                    (e.target.readOnly = true)
                  }
                />
                <button
                  onClick={() => {
                    onStartDateChange(null);
                    onEndDateChange(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t("back.management.component.endDate")}
              </label>
              <div className="flex items-center gap-2">
                <ReactDatePicker
                  selected={endDate}
                  onChange={onEndDateChange}
                  minDate={startDate ?? undefined}
                  maxDate={
                    useCase === "appointment"
                      ? undefined
                      : (currentDate ?? undefined)
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  placeholderText={t("back.management.component.selectEndDate")}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                    (e.target.readOnly = true)
                  }
                />
                <button
                  onClick={() => onEndDateChange(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className="rounded-md bg-red-500 px-4 py-2 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                onClick={handleRemoveDateFilters}
              >
                {t("back.management.component.removeDateFilters")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DateFilterButton;
